import { message } from '@/components/Message';
import { BaseInfoContext } from '@/context/BaseInfoContext';
import Login from '@/root/pages/Login';
import { useLogout } from '@/utils/app';
import { Alert, Typography } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { useWsConnection, WS_CLOSE_REASON } from '../../../api/ws';
import Modal from '@/components/Modal';

enum RequireLoginMode {
  Never,
  Required,
  RequiredAdminChanged,
}

const KickAwayTextMap = {
  [WS_CLOSE_REASON.SESSION_DUPLICATED]: {
    title: '您已在其它设备登录',
    desc: ({ nickname }) => (
      <>
        系统检测到您的当前账户 <Typography.Text strong>{nickname}</Typography.Text> 在其它设备登录
        <br />
        已从当前设备自动退出
        <br />
        您可使用当前账户重新登录，或更换登录账户
      </>
    ),
  },
  [WS_CLOSE_REASON.SESSION_EXPIRED]: {
    title: '您的登录已过期',
    desc: ({ nickname }) => (
      <>
        系统与当前账户 <Typography.Text strong>{nickname}</Typography.Text> 的会话已过期，请重新登录
      </>
    ),
  },
};

const alertStyleInModal: React.CSSProperties = {
  borderRight: 0,
  borderLeft: 0,
  borderTop: 0,
};

/**
 * 被服务器踢下线交互弹窗，场景包括:
 * 1. 会话过期
 * 2. 多设备登录同一账号
 */
const KickAwayModal: React.FC<{}> = ({}) => {
  const [{ staffId, nickname }] = useContext(BaseInfoContext);
  const [requireLogin, setRequireLogin] = useState<RequireLoginMode>(RequireLoginMode.Never);
  const { logout: logoutSilent } = useLogout({ silent: true });
  // ws 连接
  const [kickAwayReason, setKickAwayReason] = useState<WS_CLOSE_REASON>();
  const { reconnect: restartWsConnection } = useWsConnection({
    onKickAway: reason => {
      setKickAwayReason(reason);
    },
  });

  useEffect(() => {
    if (kickAwayReason === WS_CLOSE_REASON.SESSION_EXPIRED) {
      // 如果是会话过期，那么已经登出了，直接要求登录
      setRequireLogin(RequireLoginMode.Required);
      return;
    } else if (kickAwayReason === WS_CLOSE_REASON.SESSION_DUPLICATED) {
      // 如果是会话重复，那么需要静默登出，然后要求登录
      logoutSilent()
        .then(() => {
          setRequireLogin(RequireLoginMode.Required);
        })
        .catch();
    }
  }, [kickAwayReason]);

  useEffect(() => {
    if (requireLogin === RequireLoginMode.RequiredAdminChanged) {
      setTimeout(() => {
        window.location.reload();
      }, 3000);
      return;
    }
  }, [requireLogin]);

  return (
    <Modal
      destroyOnHidden
      open={Boolean(kickAwayReason)}
      closable={false}
      closeIcon={null}
      style={{ overflow: 'hidden', borderRadius: 6, padding: 0 }}
      styles={{ body: { padding: 0, background: '#fff' }, content: { background: 'transparent' } }}
      footer={null}>
      {kickAwayReason && requireLogin !== RequireLoginMode.RequiredAdminChanged && (
        <Alert
          className="mb-s"
          style={alertStyleInModal}
          type={'info'}
          showIcon
          message={KickAwayTextMap[kickAwayReason].title}
          description={KickAwayTextMap[kickAwayReason].desc({ nickname })}
        />
      )}

      {requireLogin === RequireLoginMode.RequiredAdminChanged && (
        <Alert
          type={'success'}
          style={alertStyleInModal}
          showIcon
          message="登录成功"
          description={<>登录帐号已更改，即将重新进入系统...</>}
        />
      )}

      {requireLogin === RequireLoginMode.Required && (
        <Login
          mode={'modal'}
          onSuccess={loginInfo => {
            console.log(loginInfo, staffId);
            message.success(`登录成功`);
            if (loginInfo.staffId !== staffId) {
              // 更换了登录用户
              setRequireLogin(RequireLoginMode.RequiredAdminChanged);
            } else {
              setKickAwayReason(null);
              setRequireLogin(RequireLoginMode.Never);
              restartWsConnection();
            }
          }}
        />
      )}
    </Modal>
  );
};

export default KickAwayModal;
